import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { Search, Users } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Pagination } from '@/components/Pagination'
import { useCustomers } from '@/hooks/useCustomers'
import { CustomerFilterParams } from '@/types/api'

export function CustomersPage() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<CustomerFilterParams>({
    limit: 25,
    offset: 0,
    sort_by: 'converted_at',
    sort_order: 'desc',
  })
  const [searchTerm, setSearchTerm] = useState('')

  const { data: customers, isLoading } = useCustomers({
    ...filters,
    search: searchTerm || undefined,
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground">
            View all converted leads and their referral history
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Search className="h-4 w-4" />
            Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative max-w-md">
            <Search className="absolute left-1 top-1/2 -translate-y-1/2 h-2 w-2 text-muted-foreground pointer-events-none z-10" />
            <Input
              placeholder="Search by name, phone, email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setFilters((prev) => ({ ...prev, offset: 0 }))
              }}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead className="hidden md:table-cell">Company</TableHead>
                    <TableHead className="hidden md:table-cell">Converted</TableHead>
                    <TableHead>Referrals</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers?.items.map((customer) => (
                    <TableRow
                      key={customer.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <TableCell className="font-medium">
                        {customer.first_name} {customer.last_name}
                      </TableCell>
                      <TableCell>{customer.phone || '-'}</TableCell>
                      <TableCell className="hidden md:table-cell">{customer.email || '-'}</TableCell>
                      <TableCell className="hidden md:table-cell">{customer.company_name || '-'}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {format(new Date(customer.converted_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            customer.referral_count > 0 ? 'success' : 'secondary'
                          }
                        >
                          <Users className="mr-1 h-3 w-3" />
                          {customer.referral_count}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!customers?.items || customers.items.length === 0) && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground"
                      >
                        No customers found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Pagination
                total={customers?.total || 0}
                limit={filters.limit || 25}
                offset={filters.offset || 0}
                onPageChange={(offset) =>
                  setFilters((prev) => ({ ...prev, offset }))
                }
                onLimitChange={(limit) =>
                  setFilters((prev) => ({ ...prev, limit, offset: 0 }))
                }
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
